import { useCallback, useState } from 'react';
import type { JSX, PropsWithChildren } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';

import { useDLocal, useDLocalPermanentPaymentMethod } from 'src/app/shared/hooks/payments';
import { useSession } from 'src/app/shared/hooks/session';
import type { Field, SmartFieldEvent, TokenizationData } from 'src/app/shared/utils/payments';
import { getDlocalToken } from 'src/app/shared/utils/payments';

import { DLocalCreditCardForm } from './DLocalCreditCardForm';
import type { DLocalValidationError } from './types';

type Props = {
	visible: boolean;
	beforeToken: () => void;
	onTokenError: (err: Error) => void;
	onTokenInvalid: (err: Error) => void;
	onSavePaymentError: (err: Error) => void;
	validate: () => boolean;
	withToken: (paymentType: string, token?: TokenizationData) => void;
	disabled: boolean;
	shouldSavePaymentMethod: boolean;
	nationalId: string;
	loading: boolean;
	onCardNumberFieldFocus: () => void;
	onCardExpiryFieldFocus: () => void;
	onCardCVCFieldFocus: () => void;
	onCardHolderNameFocus: () => void;
	fetchPermanentToken?: boolean;
	useDonationLanguage: boolean;
};

// eslint-disable-next-line max-lines-per-function
export function DLocalCreditCardElements({
	children,
	visible,
	beforeToken,
	onTokenError,
	onTokenInvalid,
	onSavePaymentError,
	validate,
	withToken,
	disabled,
	shouldSavePaymentMethod,
	nationalId,
	loading: parentLoading,
	onCardNumberFieldFocus,
	onCardExpiryFieldFocus,
	onCardCVCFieldFocus,
	onCardHolderNameFocus,
	fetchPermanentToken,
	useDonationLanguage,
}: PropsWithChildren<Props>): JSX.Element {
	const { sdk } = useDLocal();
	const { translate } = useI18n();
	const session = useSession();
	const [pan, setPan] = useState<Field | null>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<DLocalValidationError>({ card: undefined, cvv: undefined, expire: undefined });
	const [cardHolder, setCardHolder] = useState<string | undefined>(session.user?.displayName);
	const preparePermanentPaymentMethod = useDLocalPermanentPaymentMethod();

	const handleSubmit = useCallback(
		// eslint-disable-next-line max-statements
		async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
			event.preventDefault();
			beforeToken();
			if (!validate()) {
				return;
			}
			setLoading(true);
			const { token, error: dlocalError } = await getDlocalToken(cardHolder, pan, sdk);
			setLoading(false);
			if (dlocalError?.message) {
				onTokenError(dlocalError.message as unknown as Error);
				return;
			}
			if (dlocalError?.error) {
				setError((errors) => ({
					...errors,
					card: translate('fe.errors.credit_card.number'),
				}));
				return;
			}
			if (!token) {
				onTokenInvalid(new Error('Failed to get a valid dLocal token'));
				return;
			}

			if (fetchPermanentToken || shouldSavePaymentMethod) {
				try {
					setLoading(true);
					const result = await preparePermanentPaymentMethod({
						token,
						tokenType: 'temporary',
						shouldSavePaymentMethod,
						usage: 'ON_SESSION',
						customer: {
							name: cardHolder as string,
							countryCode: session.country.countryCode,
							document: nationalId,
							email: session.user?.email as string,
						},
					});
					withToken('creditCard', result);
					setLoading(false);
				} catch (setupError) {
					setLoading(false);
					onSavePaymentError(setupError as Error);
				}
			} else {
				withToken('creditCard', {
					token,
					tokenType: 'temporary',
				});
			}
		},
		[
			beforeToken,
			validate,
			cardHolder,
			pan,
			sdk,
			fetchPermanentToken,
			shouldSavePaymentMethod,
			onTokenError,
			translate,
			onTokenInvalid,
			preparePermanentPaymentMethod,
			session.country.countryCode,
			session.user,
			nationalId,
			onSavePaymentError,
			withToken,
		],
	);

	const updateError = useCallback((field: string, event: SmartFieldEvent) => {
		const errorMessage = event.error ? event.error.message : undefined;
		setError((errors) => ({
			...errors,
			[field]: errorMessage,
		}));
	}, []);

	return (
		<DLocalCreditCardForm
			visible={visible}
			handleSubmit={handleSubmit}
			cardHolder={cardHolder}
			onChangeCardHolderName={setCardHolder}
			error={error}
			disabled={disabled || loading}
			loading={parentLoading || loading}
			useDonationLanguage={useDonationLanguage}
			onCardHolderNameFocus={onCardHolderNameFocus}
			onCardExpiryFieldFocus={onCardExpiryFieldFocus}
			onCardCVCFieldFocus={onCardCVCFieldFocus}
			onCardNumberFieldFocus={onCardNumberFieldFocus}
			updateError={updateError}
			setPan={setPan}
		>
			{children}
		</DLocalCreditCardForm>
	);
}
